import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { IExecutionOptions } from '@kurtosys/types/query/IExecutionOptions.js';
import { IQueryOptions } from '@kurtosys/types/query/IQueryOptions.js';
import { IQueryOptionsJoin } from '@kurtosys/types/query/options/IQueryOptionsJoin.js';
import { ITransformOptions } from '@kurtosys/types/query/transforms/ITransformOptions.js';

import { commonUtils, helpers } from '../../common/index.js';
import { getQueryClass } from '../queryUtils/getQueryClass.js';
import { Transform } from '../transform/Transform.js';

import { BaseQuery } from './base/BaseQuery.js';

export class JoinQuery extends BaseQuery<IQueryOptionsJoin> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	getOptions(): IQueryOptionsJoin {
		const { joinOptions, subQueries, options = {} } = this.queryOptions;
		const { joins, sourceQueryId, type } = joinOptions || {};
		return {
			sourceQueryId,
			joins,
			type,
			subQueries,
			...(options.join || {}),
		};
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const { type = 'combine' } = this.options;
		if (type === 'combine') {
			return this.getCombineRawValue<T>(executionOptions);
		}
		if (type === 'reduce') {
			return this.getReduceRawValue<T>(executionOptions);
		}
	}
	getReduceRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const { subQueries = [] } = this.options;
		const response: any[] = [];
		subQueries.forEach((subQueryOptions): void => {
			const subQuery = getQueryClass(this.dependencies, subQueryOptions, this.translationHelper);
			if (subQuery) {
				const subQueryValue = subQuery.execute(executionOptions);
				if (subQueryValue) {
					response.push(...this.convertToArray(subQueryValue));
				}
			}
		});
		return response as any as T;
	}
	getCombineRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const { subQueries, sourceQueryId: initialQueryId, joins = [] } = this.options;
		if (!initialQueryId || !joins || joins.length === 0) {
			return [] as unknown as T;
		}
		const subQueriesById: { [key: string]: any } = {};
		for (const subQueryIndex in subQueries) {
			const subQueryOptions = subQueries[subQueryIndex];
			const { queryId = `subQuery${subQueryIndex}` } = subQueryOptions;
			const subQuery = getQueryClass(this.dependencies, subQueryOptions, this.translationHelper);
			if (subQuery) {
				const subQueryValue = subQuery.execute(executionOptions);
				subQueriesById[queryId] = this.convertToArray(subQueryValue);
			}
		}
		let response: any[] = subQueriesById[initialQueryId] || [];
		for (const join of joins) {
			const {
				destinationKey,
				destinationQueryId,
				sourceKey,
				type = 'optional',
				rowTransforms,
				valueTransforms,
			} = join;
			const sourceCollection = [...response];
			const destinationCollection = subQueriesById[destinationQueryId] || [];
			response = [];
			for (const sourceRow of sourceCollection) {
				const sourceValue = commonUtils.get(sourceRow, sourceKey);
				let hasMatchingRow = false;
				destinationCollection.forEach((destinationRow): void => {
					destinationRow = this.applyTranforms(destinationRow, rowTransforms, executionOptions);
					const destinationValue = this.applyTranforms(
						commonUtils.get(destinationRow, destinationKey),
						valueTransforms,
						executionOptions,
					);
					const isMatch = sourceValue === destinationValue;
					if (isMatch) {
						hasMatchingRow = true;
					}
					if (isMatch || type === 'outer') {
						response.push(
							commonUtils.deepMergeObjectsWithOptions(
								{ arrayMergeStrategy: 'DeepMerge' },
								sourceRow,
								destinationRow,
							),
						);
					}
				});
				if (type === 'optional' && !hasMatchingRow) {
					response.push(sourceRow);
				}
			}
		}
		return response as unknown as T;
	}
	convertToArray = (value: any): any[] => {
		if (typeof value !== 'undefined' && !commonUtils.isArrayLike(value)) {
			return [value];
		}
		return value;
	};
	applyTranforms = (value: any, transforms: ITransformOptions[], executionOptions: IExecutionOptions): any => {
		if (transforms) {
			for (const transformOptions of transforms) {
				const transform = new Transform(this.dependencies, transformOptions, this.translationHelper);
				value = transform.execute(value, this.queryOptions, executionOptions);
			}
		}
		return value;
	};
}
